import React, { createContext, useContext, useState, useEffect } from 'react';
import { defaultSiteData } from '../data/defaultSiteData';

const SiteDataContext = createContext();

export const useSiteData = () => {
    const context = useContext(SiteDataContext);
    if (!context) {
        throw new Error('useSiteData must be used within a SiteDataProvider');
    }
    return context;
};

export const SiteDataProvider = ({ children }) => {
    const [siteData, setSiteData] = useState(() => {
        try {
            const storedData = localStorage.getItem('mediseller_site_data');
            if (storedData) {
                const parsed = JSON.parse(storedData);
                // Ensure new critical top-level structures like categoryPages exist
                const baseData = { ...defaultSiteData };

                // Keep existing user modifications but layer them over the latest defaults
                const merged = {
                    ...baseData,
                    ...parsed,
                };

                // Merge categories array based on ID to ensure new categories appear
                const mergedCategoriesList = [...baseData.categories];
                (parsed.categories || []).forEach(parsedCat => {
                    const index = mergedCategoriesList.findIndex(c => c.id === parsedCat.id);
                    if (index > -1) {
                        mergedCategoriesList[index] = { ...mergedCategoriesList[index], ...parsedCat };
                    }
                });
                merged.categories = mergedCategoriesList;

                // Specifically ensure that if 'categoryPages' was missing in old LocalStorage, it gets added now.
                if (!parsed.categoryPages) {
                    merged.categoryPages = baseData.categoryPages;
                } else {
                    // Merge individual category keys in case new ones were added to defaultSiteData
                    const mergedCategoryPages = { ...baseData.categoryPages };
                    for (const key in parsed.categoryPages) {
                        mergedCategoryPages[key] = {
                            ...(baseData.categoryPages[key] || {}),
                            ...parsed.categoryPages[key],
                        };

                        // Critical fix: Smart merge products by ID to ensure new mock data appears
                        const baseProducts = baseData.categoryPages[key]?.products || [];
                        const parsedProducts = parsed.categoryPages[key]?.products || [];

                        const mergedProducts = [...parsedProducts];
                        baseProducts.forEach(bp => {
                            if (!mergedProducts.find(p => p.id === bp.id)) {
                                mergedProducts.push(bp);
                            }
                        });
                        mergedCategoryPages[key].products = mergedProducts;
                    }
                    merged.categoryPages = mergedCategoryPages;
                }

                return merged;
            }
            return defaultSiteData;
        } catch (error) {
            console.error('Error reading site data from localStorage:', error);
            return defaultSiteData;
        }
    });

    // Verify updates
    useEffect(() => {
        // If the default data gains new keys (e.g. from an app update), we merge them in.
        // This is a naive deep merge to ensure new fields are caught.
        const mergedData = { ...defaultSiteData, ...siteData };

        // We only save basic top-level mergers here. A deep merge function could be added later if missing fields become an issue.
        // For now we trust what's in localstorage unless it's null.

        if (!localStorage.getItem('mediseller_site_data')) {
            localStorage.setItem('mediseller_site_data', JSON.stringify(defaultSiteData));
        }
    }, [siteData]);

    const saveSiteData = (newData) => {
        try {
            setSiteData(newData);
            localStorage.setItem('mediseller_site_data', JSON.stringify(newData));
            return true;
        } catch (error) {
            console.error('Error saving site data to localStorage:', error);
            return false;
        }
    };

    const resetSiteData = () => {
        try {
            setSiteData(defaultSiteData);
            localStorage.removeItem('mediseller_site_data');
            return true;
        } catch (error) {
            console.error("Error resetting data", error);
            return false;
        }
    };

    return (
        <SiteDataContext.Provider value={{ siteData, saveSiteData, resetSiteData }}>
            {children}
        </SiteDataContext.Provider>
    );
};
